/**
 * Typed signatures — a name rendered onto the signature canvas in a script
 * face, as an alternative to drawing one by hand.
 *
 * The output is deliberately the SAME thing a drawn signature produces: a PNG
 * data URL from `canvas.toDataURL()`. Everything downstream — the required-field
 * validator, `serializeField`, the upload to the monday file column, and the
 * stamper that draws the image onto the PDF — is untouched and cannot tell the
 * two apart. That is what keeps this a purely presentational addition rather
 * than a second signature format to carry through the whole stack.
 */

/**
 * Script faces, in preference order, ending in the generic `cursive`.
 *
 * No web font is loaded. That is a deliberate trade: fetching one would add a
 * network dependency to the one control a respondent must complete to submit,
 * and a font that fails to arrive would silently render the name in the browser
 * default — a "signature" in plain sans-serif.
 *
 * The consequence is that the exact face depends on the operating system
 * (Snell Roundhand on Apple, Segoe Script on Windows, whatever `cursive` maps
 * to elsewhere). That is acceptable here because the respondent sees the
 * rendered result before submitting and the PNG is captured from that same
 * canvas: what they approved is exactly what is stored.
 */
export const SIGNATURE_FONT_STACK =
  '"Snell Roundhand", "Segoe Script", "Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive';

/** Fraction of the canvas width a typed name may occupy before it is shrunk. */
const MAX_WIDTH_RATIO = 0.9;

/** Font size as a fraction of canvas height, before any shrink-to-fit. */
const START_SIZE_RATIO = 0.45;

const MIN_FONT_SIZE = 12;

/**
 * Draw `name` onto `canvas` as a signature, replacing whatever is there.
 *
 * Shrinks the face until the name fits the canvas width, so a long name stays
 * inside the box instead of running off the edge and being cropped by the PNG.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} name
 * @param {string} [color]  stroke colour, matching the drawn pad's ink
 * @returns {string} a PNG data URL, or '' when the name is blank
 */
export function renderTypedSignature(canvas, name, color = '#000') {
  if (!canvas) return '';
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const text = (name || '').trim();
  if (!text) return '';

  const maxWidth = canvas.width * MAX_WIDTH_RATIO;
  let size = Math.max(MIN_FONT_SIZE, canvas.height * START_SIZE_RATIO);

  // Italic reads as handwriting even when the fallback face is not itself a
  // script — the generic `cursive` is a plain sans on some Android builds.
  const fontAt = (px) => `italic ${px}px ${SIGNATURE_FONT_STACK}`;

  ctx.font = fontAt(size);
  while (ctx.measureText(text).width > maxWidth && size > MIN_FONT_SIZE) {
    size -= 1;
    ctx.font = fontAt(size);
  }

  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2, maxWidth);

  return canvas.toDataURL();
}

/** Blank the canvas without drawing anything. */
export function clearSignatureCanvas(canvas) {
  if (!canvas) return;
  canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
}
